// Ko'rinish statistikasi (Q-031): qaysi mavzu va rang ko'proq yoqadi. Faqat platforma admini ko'radi.
// Ma'lumot anonim: mavzu, tanlov, rol turi va vaqt (ism, telefon, savdo yo'q).
// "O'zi tanlagan" va "standartda qolgan" alohida — tegilmagan sozlama "yoqdi" degani emas.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ACCENTS, Alert, Badge, Card, EmptyState, Icon, Spinner, THEME_LABEL, useAsync } from '@onepos/ui';
import type { PersonalTheme } from '@onepos/ui';
import { api } from '../lib/api';
import { fmtDate } from '../lib/format';

interface UiStats {
  days: number;
  themes: { theme: string; source: 'user' | 'restaurant' | 'default'; n: number }[];
  choices: { choice: string; n: number }[];
  surfaces: { surface: string; theme: string; n: number }[];
  roles: { role: string; theme: string; n: number }[];
  switches: { from: string; to: string; n: number; stayed: number }[];
  restaurants: {
    id: string;
    name: string;
    status: string;
    theme: string;
    theme_set: boolean;
    accent: string;
    logo: boolean;
    users: Record<string, number>;
    last_seen: string | null;
  }[];
}

const THEMES = ['modern', 'dark', 'classic', 'contrast'];
const SURFACE: Record<string, string> = { panel: 'Boshqaruv paneli', kassa: 'Kassa', kds: 'Oshxona ekrani' };
const ROLE: Record<string, string> = { owner: 'Ega', manager: 'Menejer', cashier: 'Kassir', cook: 'Oshpaz', waiter: 'Ofitsiant', custom: 'O‘zi yaratgan rol' };
const label = (t: string) => THEME_LABEL[t as PersonalTheme] ?? t;
const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/** Bitta ko'rsatkich: son, ulush va ingichka chiziq (bitta rang — o'lcham, rang emas) */
function Share({ n, total }: { n: number; total: number }) {
  const p = pct(n, total);
  return (
    <span className="cn-share" title={`${n} ta — ${p}%`}>
      <span className="cn-share__bar" aria-hidden="true">
        <span style={{ width: `${p}%` }} />
      </span>
      <span className="op-num">
        {n} <span className="cn-muted">· {p}%</span>
      </span>
    </span>
  );
}

export function UiStatsPage() {
  const [days, setDays] = useState(30);
  const { data, error, loading, reload } = useAsync(() => api.rpc<UiStats>('platform_ui_stats', { p_days: days }), [days]);

  const bySource = (source: string) => THEMES.map((t) => data?.themes.find((x) => x.theme === t && x.source === source)?.n ?? 0);
  const chosen = bySource('user');
  const byRestaurant = bySource('restaurant');
  const untouched = bySource('default');
  const total = THEMES.reduce((s, _, i) => s + chosen[i]! + byRestaurant[i]! + untouched[i]!, 0);
  const chosenTotal = chosen.reduce((a, b) => a + b, 0);
  const restaurants = data?.restaurants ?? [];
  const accentCount = Object.keys(ACCENTS).map((a) => [a, restaurants.filter((r) => r.accent === a).length] as const);
  const roles = [...new Set((data?.roles ?? []).map((r) => r.role))];

  return (
    <>
      <header className="cn-page-head">
        <div>
          <h1>Ko‘rinish statistikasi</h1>
          <p>Qaysi mavzu va rang ko‘proq yoqadi. Faqat oxirgi {days} kunda dasturni ochganlar sanaladi; ism va telefon yozilmaydi.</p>
        </div>
        <div className="cn-row" style={{ gap: 6 }}>
          <div className="cn-seg" role="radiogroup" aria-label="Davr">
            {[7, 30, 90].map((d) => (
              <button key={d} type="button" role="radio" aria-checked={days === d} className={`cn-seg__btn${days === d ? ' cn-seg__btn--on' : ''}`} onClick={() => setDays(d)}>
                {d} kun
              </button>
            ))}
          </div>
          <button type="button" className="op-btn op-btn--secondary op-btn--sm" onClick={reload} disabled={loading}>
            <Icon name="refresh" /> Yangilash
          </button>
        </div>
      </header>

      {error && <Alert tone="danger">{error.message}</Alert>}
      {loading && !data ? (
        <Spinner />
      ) : !data || total === 0 ? (
        <Card>
          <EmptyState title="Hali ma’lumot yo‘q">Xodimlar kassa, panel yoki oshxona ekranini ochganda shu yerda ko‘rinadi.</EmptyState>
        </Card>
      ) : (
        <>
          <section className="cn-stats" aria-label="Ko‘rsatkichlar">
            <div className="op-card cn-stat">
              <span className="cn-stat__label">Faol foydalanuvchilar</span>
              <span className="cn-stat__value op-num">{total}</span>
              <span className="cn-stat__hint">xodim × yuza (kassa, panel, oshxona)</span>
            </div>
            <div className="op-card cn-stat">
              <span className="cn-stat__label">O‘zi tanlaganlar</span>
              <span className="cn-stat__value op-num">{chosenTotal}</span>
              <span className="cn-stat__hint">{pct(chosenTotal, total)}% — haqiqiy fikr shu yerda</span>
            </div>
            <div className="op-card cn-stat">
              <span className="cn-stat__label">Eng ko‘p tanlangan</span>
              <span className="cn-stat__value">{chosenTotal ? label(THEMES[chosen.indexOf(Math.max(...chosen))]!) : '—'}</span>
              <span className="cn-stat__hint">xodimlar o‘zi tanlagan mavzu</span>
            </div>
          </section>

          <Card title="Mavzular">
            <div className="cn-table-wrap">
              <table className="cn-table">
                <thead>
                  <tr>
                    <th>Mavzu</th>
                    <th>O‘zi tanlagan</th>
                    <th>Restoran standarti</th>
                    <th>Tegilmagan</th>
                  </tr>
                </thead>
                <tbody>
                  {THEMES.map((t, i) => (
                    <tr key={t}>
                      <td>
                        <span className="cn-table__name">{label(t)}</span>
                      </td>
                      <td data-label="O‘zi tanlagan">
                        <Share n={chosen[i]!} total={chosenTotal} />
                      </td>
                      <td data-label="Restoran standarti" className="op-num">
                        <span>{byRestaurant[i]}</span>
                      </td>
                      <td data-label="Tegilmagan" className="op-num">
                        <span>{untouched[i]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="cn-muted" style={{ marginBottom: 0 }}>
              “Tegilmagan” — hech narsa o‘zgartirmagan (telefonga qarab zamonaviy yoki tungi). Bu “yoqdi” degani emas.
            </p>
          </Card>

          <div className="cn-two">
            <Card title="Almashtirishlar">
              {data.switches.length === 0 ? (
                <p className="cn-muted">Bu davrda hech kim mavzuni almashtirmagan.</p>
              ) : (
                <ul className="cn-list cn-list--kv">
                  {data.switches.map((s) => (
                    <li key={`${s.from}-${s.to}`}>
                      <span>
                        {label(s.from)} → <b>{label(s.to)}</b>
                      </span>
                      <span className="op-num">
                        {s.n} marta · {s.stayed} tasi qoldi <Badge tone={s.stayed * 2 >= s.n ? 'success' : 'warning'}>{pct(s.stayed, s.n)}%</Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="cn-muted" style={{ marginBottom: 0 }}>
                “Qoldi” — o‘tgan xodim hozir ham o‘sha mavzuda. Ko‘p o‘tib, qolishsa — mavzu yoqqan.
              </p>
            </Card>
            <Card title="Qayerda va kim">
              <ul className="cn-list cn-list--kv">
                {Object.keys(SURFACE).map((sf) => {
                  const rows = data.surfaces.filter((x) => x.surface === sf);
                  if (!rows.length) return null;
                  return (
                    <li key={sf}>
                      <span>{SURFACE[sf]}</span>
                      <span>{rows.map((r) => `${label(r.theme)}: ${r.n}`).join(', ')}</span>
                    </li>
                  );
                })}
                {roles.map((role) => (
                  <li key={role}>
                    <span>{ROLE[role] ?? role}</span>
                    <span>
                      {data.roles
                        .filter((r) => r.role === role)
                        .map((r) => `${label(r.theme)}: ${r.n}`)
                        .join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <Card title="Restoranlar">
            <p className="cn-muted" style={{ marginTop: 0 }}>
              Asosiy ranglar: {accentCount.filter(([, n]) => n > 0).map(([a, n]) => `${ACCENTS[a as keyof typeof ACCENTS].label} — ${n}`).join(', ') || '—'}
            </p>
            <div className="cn-table-wrap">
              <table className="cn-table">
                <thead>
                  <tr>
                    <th>Restoran</th>
                    <th>Standart mavzu</th>
                    <th>Rang</th>
                    <th>Xodimlar ko‘rinishi</th>
                    <th>Oxirgi</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link className="cn-table__name" to={`/restoranlar/${r.id}`}>
                          {r.name}
                        </Link>
                        {r.logo && <span className="cn-table__sub">logotip bor</span>}
                      </td>
                      <td data-label="Standart mavzu">
                        <span>
                          {label(r.theme)}
                          {!r.theme_set && <span className="cn-table__sub">tegilmagan</span>}
                        </span>
                      </td>
                      <td data-label="Rang">
                        <span>{ACCENTS[r.accent as keyof typeof ACCENTS]?.label ?? r.accent}</span>
                      </td>
                      <td data-label="Xodimlar ko‘rinishi">
                        <span>
                          {Object.entries(r.users)
                            .map(([t, n]) => `${label(t)}: ${n}`)
                            .join(', ') || '—'}
                        </span>
                      </td>
                      <td data-label="Oxirgi" className="op-num">
                        <span>{r.last_seen ? fmtDate(r.last_seen) : '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
